import React, { useCallback, useState, useMemo } from "react";
import { groupBudgetsEntriesBySegment, sortData, toFlatArray } from "./pacingUtils";
import PacingBar from "./PacingBar";
import {
  Button as BPMButton,
  ButtonType,
  DownloadDropdown,
  Dropdown,
  DropdownToggleType,
  GraphTableToggleButton,
  TextToggleButton,
} from "../Components";
import { ButtonFrameworkVariant } from "../Components/ButtonFramework";
import { MdAutoGraph } from "react-icons/md";
import CollapseButton from "../Components/CollapseButton";
import { Container, Modal, Col, Row } from "react-bootstrap";
import ModalCustomLegend from "./ModalCustomLegend";
import { downloadPNG } from "../utils/download-utils";
import ModalCumulativeAndDailyGraph from "./ModalCumulativeAndDailyGraph";
import "./PacingBarsDashboard.scss";
import ModalCumulativeAndDailyTable from "./ModalCumulativeAndDailyTable";
import { PacingDataEntry } from "@blisspointmedia/bpm-types/dist/Pacing";
import { DateGroupingOption } from "../TVADCrossChannel/homePageConstants";
import * as R from "ramda";

export const DATE_GROUPING_OPTIONS: DateGroupingOption[] = [{ value: "Day", label: "Daily" }];

interface PacingBarsDashboardProps {
  pacingData: PacingDataEntry[];
  keys: string[];
  simplicity: string;
  marginLeft: number;
  segmentVisibility: { [key: string]: boolean };
  toggleSegmentVisibility: (key: string) => void;
  longestText: number;
  sortOption: string;
  total?: boolean;
  dateEnd: string;
  goalName: string;
  isActual: boolean;
  combineSections?: boolean;
  level?: number;
}

const PacingBarsDashboard: React.FC<PacingBarsDashboardProps> = ({
  pacingData,
  keys,
  simplicity,
  marginLeft,
  segmentVisibility,
  toggleSegmentVisibility,
  longestText,
  sortOption,
  total = false,
  dateEnd,
  goalName,
  isActual,
  combineSections = false,
  level = 1,
}) => {
  const [activeModal, setActiveModal] = useState<String>("");
  const [displayMode, setDisplayMode] = useState("Cumulative");
  const [graphView, setGraphView] = useState(true);
  const [chartDateGrouping, setChartDateGrouping] = useState("Day");

  const combinedClassGoalName = useMemo(
    () => (R.equals(keys, ["goalName"]) && combineSections ? " combinedGoalName" : ""),
    [combineSections, keys]
  );
  const combinedClassSegments = useMemo(
    () => (!R.equals(keys, ["goalName"]) && combineSections ? " combinedSegments" : ""),
    [combineSections, keys]
  );

  const pngDownloadPacingData = useCallback(async () => {
    await downloadPNG(".modal-body .container", "pacingChart");
  }, []);

  if (keys.length === 0) {
    return null;
  }

  const currentKey = keys[0];
  const remainingKeys = keys.slice(1);
  const groupedData = groupBudgetsEntriesBySegment(pacingData, [currentKey], goalName);
  const sortedGroupedData = sortData(groupedData, sortOption, currentKey);
  const propertiesToGroupGraph = [currentKey, "date"];

  const finalSortedGroupedFilteredData = sortedGroupedData.filter(
    item => item[currentKey] !== null
  );

  const maxBookedSimpleSpend = Math.max(
    ...finalSortedGroupedFilteredData.map(item =>
      simplicity === "simple" ? item.simpleBooked : item.adjustedBooked
    )
  );

  return (
    <div
      className={`${
        keys.length === 1 && marginLeft === 1 ? "dashboardChartContainer" : ""
      } ${combinedClassGoalName} ${combinedClassSegments} pacingBarLevel${level}`}
    >
      {finalSortedGroupedFilteredData.map((group, index) => (
        <div
          key={index}
          className={marginLeft === 1 && keys.length > 1 ? "dashboardChartContainer" : ""}
        >
          {marginLeft === 1 && keys.length > 1 && (
            <div className="chartsContainerBodyHeader">
              <div className="containerBodyHeaderTitle">{group[currentKey]}</div>
              <CollapseButton onClick={() => toggleSegmentVisibility(group[currentKey])} />
            </div>
          )}
          {marginLeft > 1 && (
            <div hidden>
              {" "}
              <div>{group[currentKey]}</div>
              <CollapseButton onClick={() => toggleSegmentVisibility(group[currentKey])} />
            </div>
          )}
          <div className="chartsContainerBody">
            <PacingBar
              className="simpleBarChart"
              pacingData={{
                totalCleared: simplicity === "simple" ? group.simpleCleared : group.adjustedCleared,
                totalProjected:
                  simplicity === "simple" ? group.simpleProjected : group.adjustedProjected,
                totalRemainder:
                  simplicity === "simple"
                    ? group.simpleBooked - (group.simpleCleared + group.simpleProjected)
                    : group.adjustedBooked - (group.adjustedCleared + group.adjustedProjected),
                segmentName: group[currentKey],
                budget: simplicity === "simple" ? group.simpleBooked : group.adjustedBooked,
              }}
              keys={["totalCleared", "totalProjected", "totalRemainder"]}
              marginLeft={marginLeft}
              numberOfSegments={keys}
              longestText={longestText}
              total={total}
              currentSegment={currentKey}
              maxBudget={maxBookedSimpleSpend}
              goalName={goalName}
              isActual={isActual}
            />
            <div className="buttonGraph">
              <BPMButton
                size="lg"
                type={ButtonType.FILLED}
                variant={ButtonFrameworkVariant.LEADING_ICON}
                design="secondary"
                icon={<MdAutoGraph />}
                onClick={() => setActiveModal(group[currentKey])}
              >
                Details
              </BPMButton>
            </div>
          </div>

          {!segmentVisibility[group[currentKey]] &&
          (sortedGroupedData.length - 1 !== index || keys.length > 1) ? (
            <div className={`lineAtBotBase lineAtBot${marginLeft}`} />
          ) : null}

          {!segmentVisibility[group[currentKey]] && (
            <>
              <PacingBarsDashboard
                pacingData={pacingData.filter(item => {
                  const itemValue = toFlatArray(item[currentKey]);
                  const testValue = toFlatArray(group[currentKey]);
                  return itemValue.some(val => testValue.includes(val));
                })}
                keys={remainingKeys}
                simplicity={simplicity}
                marginLeft={marginLeft + 1}
                segmentVisibility={segmentVisibility}
                toggleSegmentVisibility={toggleSegmentVisibility}
                longestText={longestText}
                sortOption={sortOption}
                dateEnd={dateEnd}
                goalName={goalName}
                isActual={isActual}
                level={level + 1}
              />
            </>
          )}
          {activeModal === group[currentKey] && (
            <Modal show={true} onHide={() => setActiveModal("")} size="xl" className="pacingModal">
              {" "}
              <Modal.Header closeButton>
                <div className="pacingModalHeader">
                  <div className="pacingModalHeaderButtons">
                    {" "}
                    <Dropdown
                      type={DropdownToggleType.WIDGET_TITLE}
                      value={chartDateGrouping}
                      options={DATE_GROUPING_OPTIONS}
                      onChange={option => setChartDateGrouping(option)}
                    />
                    {total ? "" : group[currentKey]}
                    {` ${goalName}`}
                  </div>
                  <div className="pacingModalHeaderButtons">
                    <TextToggleButton
                      options={graphView ? ["Cumulative", "Daily"] : ["Daily"]}
                      selectedOption={graphView ? displayMode : "Daily"}
                      onChange={value => {
                        setDisplayMode(value);
                      }}
                    />
                    <GraphTableToggleButton
                      graphView={graphView}
                      setGraphView={setGraphView}
                      widgetName="pacing_bars_dashboard"
                    />
                    <DownloadDropdown
                      size="sm"
                      onClickOptions={[pngDownloadPacingData]}
                      menuOptions={["PNG"]}
                    />
                  </div>
                </div>
              </Modal.Header>
              <Modal.Body>
                {graphView ? (
                  <Container>
                    <Row xs={1} md={10}>
                      <PacingBar
                        className="simpleBarChart"
                        pacingData={{
                          totalCleared:
                            simplicity === "simple" ? group.simpleCleared : group.adjustedCleared,
                          totalProjected:
                            simplicity === "simple"
                              ? group.simpleProjected
                              : group.adjustedProjected,
                          totalRemainder:
                            simplicity === "simple"
                              ? group.simpleBooked - (group.simpleCleared + group.simpleProjected)
                              : group.adjustedBooked -
                                (group.adjustedCleared + group.adjustedProjected),
                          segmentName: group[currentKey],
                          budget:
                            simplicity === "simple" ? group.simpleBooked : group.adjustedBooked,
                        }}
                        keys={["totalCleared", "totalProjected", "totalRemainder"]}
                        marginLeft={marginLeft}
                        numberOfSegments={keys}
                        isModal={true}
                        longestText={longestText}
                        currentSegment={currentKey}
                        goalName={goalName}
                        isActual={isActual}
                      />
                    </Row>
                    <Row xs={10} md={10}>
                      <Col xs={10} md={10}>
                        <ModalCumulativeAndDailyGraph
                          data={groupBudgetsEntriesBySegment(
                            pacingData.filter(item => {
                              const itemValue = toFlatArray(item[currentKey]);
                              const testValue = toFlatArray(group[currentKey]);
                              return itemValue.some(val => testValue.includes(val));
                            }),
                            propertiesToGroupGraph,
                            goalName
                          )}
                          today={new Date(
                            new Date().setDate(new Date().getDate() - 1)
                          ).toDateString()}
                          showCumulative={displayMode === "Cumulative"}
                          budget={
                            simplicity === "simple" ? group.simpleBooked : group.adjustedBooked
                          }
                        ></ModalCumulativeAndDailyGraph>
                      </Col>
                      <Col xs={2} md={2}>
                        <ModalCustomLegend
                          graphView={displayMode === "Cumulative"}
                        ></ModalCustomLegend>
                      </Col>
                    </Row>
                  </Container>
                ) : (
                  <Container style={{ height: "100%" }}>
                    <ModalCumulativeAndDailyTable
                      data={groupBudgetsEntriesBySegment(
                        pacingData.filter(item => {
                          const itemValue = toFlatArray(item[currentKey]);
                          const testValue = toFlatArray(group[currentKey]);
                          return itemValue.some(val => testValue.includes(val));
                        }),
                        propertiesToGroupGraph,
                        goalName
                      )}
                      showCumulative={displayMode === "Cumulative"}
                      budget={simplicity === "simple" ? group.simpleBooked : group.adjustedBooked}
                      goalName={goalName}
                    ></ModalCumulativeAndDailyTable>
                  </Container>
                )}
              </Modal.Body>
            </Modal>
          )}
        </div>
      ))}
    </div>
  );
};

export default PacingBarsDashboard;
